import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import {
  CultivationHierarchyApi
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import { CultivationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import { CommonModule } from '@angular/common';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import {
  AddressToUserApi
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address-to-user/address-to-user-api';
import {
  MyCultivationHierarchyListResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/my-cultivation-hierarchy-list.response';
import { User } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/user.entity';
import { Router } from '@angular/router';
import { StorageService } from '../../@commons/@services/front/storage.service';
import {
  CultivationHierarchy
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/cultivation-hierarchy.entity';
import {
  Cultivation
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/cultivation.entity';
import { HierarchyItemComponent } from '../@components/hierarchy-item/hierarchy-item.component';
import {
  HierarchyItemsChoiceModalComponent
} from '../@modals/hierarchy-items-choice-modal/hierarchy-items-choice-modal.component';
import { SkeletonLoaderComponent } from '../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import RoutesConstants from '../../@commons/constants/routes.constants';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import {
  PlantListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/plant-list.request';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'vw-user-hierarchies',
  imports: [SharedModule, CommonModule, TranslateModule, HierarchyItemComponent, HierarchyItemsChoiceModalComponent, SkeletonLoaderComponent],
  templateUrl: './user-hierarchies.component.html',
  styleUrls: ['./user-hierarchies.component.scss'],
  providers: [{
    provide: CultivationApi,
    useClass: CultivationApi
  }, {
    provide: CultivationHierarchyApi,
    useClass: CultivationHierarchyApi
  },
  {
    provide: UserApi,
    useClass: UserApi
  }, {
    provide: AddressToUserApi,
    useClass: AddressToUserApi
  }, {
    provide: PlantApi,
    useClass: PlantApi
  }]
})
export class UserHierarchiesComponent implements OnInit, OnChanges {
  @Input() hierarchies: MyCultivationHierarchyListResponse[] = []
  @Input() isLoading: boolean = true;
  isModalOpen: boolean = false;
  userHierarchies: MyCultivationHierarchyListResponse[] = [];
  selectedHierarchy: MyCultivationHierarchyListResponse;
  user: User;
  userAddressUid: string;

  isSelectionMode: boolean = false;
  selectedHierarchyUids: Set<string> = new Set();
  isDeleting: boolean = false;
  isConfirmDeleteOpen: boolean = false;

  constructor(private router: Router,
    private storageService: StorageService,
    private cultivationHierarchyApi: CultivationHierarchyApi,
    private addressToUserApi: AddressToUserApi,
    private plantApi: PlantApi,
    private userApi: UserApi) { }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * Fetches the list of cultivations from the service.
   */
  async ngOnInit() {

    if (this.router.url === '/garden/list') {
      await this.loadUser();
      await this.loadHierarchies();
      this.isLoading = false;
    } else {
      this.userHierarchies = this.hierarchies
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['hierarchies'] && !changes['hierarchies'].firstChange) {
      this.userHierarchies = this.hierarchies
    }
  }

  async loadUser() {
    this.user = await this.userApi.getCurrentUser();
  }

  async loadHierarchies() {
    this.userHierarchies = await this.cultivationHierarchyApi.myGarden(this.storageService.getCurrentLanguage());
  }

  /**
   * Opens the modal and sets the selected hierarchy.
   * @param hierarchy - The hierarchy to display in the modal
   */
  openModal(hierarchy: MyCultivationHierarchyListResponse) {
    // Check if varietyType is not 0 and not undefined
    if (hierarchy.cultivations && hierarchy.cultivations.length > 1) {
      this.selectedHierarchy = hierarchy;  // Set the selected cultivation
      this.isModalOpen = true;      // Show the modal
    } else {
      const cultivation = hierarchy.cultivations[0];
      const targetUrl = `/garden/cultivations/${cultivation.uid}/plants`;
      this.router.navigate([targetUrl], {
        state: {
          cultivationId: hierarchy.uid,
          varietyType: null,
          name: hierarchy.name
        }
      });
    }
  }

  /**
   * Closes the modal and resets the selected hierarchy.
   */
  closeModal() {
    this.isModalOpen = false; // Reset selected cultivation on close
    console.log('Modal has been closed');
  }

  /**
   * Handles the modal close action, calls closeModal.
   */
  onModalClosed() {
    this.closeModal();
  }

  /**
   * Confirms the action taken in the modal and closes it.
   */
  confirmAction() {
    this.closeModal();
  }

  /**
   * Navigates to the variety cultivation route when a cultivation is selected.
   */
  onCultivationSelected(hierarchy: CultivationHierarchy, cultivation: Cultivation) {
    // Navigate to the updated path structure, including the cultivationname
    const targetUrl = `/garden/cultivations/${cultivation.uid}/plants`;
    this.closeModal();
    this.router.navigate([targetUrl], {
      state: {
        hierarchyUid: hierarchy!.uid,  // Explicitly pass varietyType as well
        cultivationUid: cultivation!.uid,  // Pass the variant ID
        name: cultivation!.name,  // Pass the cultivation name
      }
    });
  }

  onAddPlant() {
    this.router.navigate([RoutesConstants.GARDEN_ADDPLANT]);
  }

  toggleSelectionMode() {
    this.isSelectionMode = !this.isSelectionMode;
    if (!this.isSelectionMode) {
      this.selectedHierarchyUids.clear();
    }
  }

  toggleSelection(hierarchy: MyCultivationHierarchyListResponse) {
    const uid = hierarchy.uid;
    if (this.selectedHierarchyUids.has(uid)) {
      this.selectedHierarchyUids.delete(uid);
    } else {
      this.selectedHierarchyUids.add(uid);
    }
  }

  isSelected(hierarchy: MyCultivationHierarchyListResponse): boolean {
    return this.selectedHierarchyUids.has(hierarchy.uid);
  }

  onHierarchyClicked(hierarchy: MyCultivationHierarchyListResponse) {
    if (this.isSelectionMode) {
      this.toggleSelection(hierarchy);
    } else {
      this.openModal(hierarchy);
    }
  }

  confirmDelete() {
    if (this.selectedHierarchyUids.size === 0) return;
    this.isConfirmDeleteOpen = true;
  }

  cancelDelete() {
    this.isConfirmDeleteOpen = false;
  }

  async deleteSelected() {
    if (this.isDeleting || this.selectedHierarchyUids.size === 0) return;
    this.isConfirmDeleteOpen = false;
    this.isDeleting = true;

    const selectedHierarchies = this.userHierarchies.filter(h => this.selectedHierarchyUids.has(h.uid));

    for (const hierarchy of selectedHierarchies) {
      for (const cultivation of hierarchy.cultivations) {
        const listPlantRequest: PlantListRequest = { cultivationUid: cultivation.uid };
        const res = await this.plantApi.list(listPlantRequest, 0, 100, this.storageService.getCurrentLanguage());
        for (const plant of res.results) {
          await this.plantApi.deleteById(plant.uid);
        }
      }
    }

    this.selectedHierarchyUids.clear();
    this.isSelectionMode = false;
    this.isDeleting = false;
    await this.loadHierarchies();
  }
}
